/**
 * High scores (docs/spec/07 §2): Classic, The Deep and Expedition tables - rank, initials, score,
 * how far, when - with a new entry flashing and an R beside scores made at Relaxed speed. The
 * tables are the player's own merged with the world's, which are fetched afresh on the way in.
 */

import type { ScoreEntry, TableId } from '../../game/hiscores';
import { LIGHT_BLUE, LIGHT_GREY, ORANGE, YELLOW } from '../../render/palette';
import type { Screen } from '../../render/screen';
import type { MenuAction, PointerInput, SceneHost } from '../scene';
import { UiScene } from '../kit/base';
import { CONTENT_TOP } from '../kit/chrome';
import { slideOffset } from '../kit/menu';
import { drawSpinner } from '../kit/spinner';
import { drawTabs, tabAt, tabRects } from '../kit/tabs';
import { commas, tableDate } from '../kit/text';
import { ACCENT, cycle, FAINT, INK, MUTED, RAINBOW } from '../kit/theme';

const TABLES: readonly { readonly id: TableId; readonly label: string }[] = [
  { id: 'classic', label: 'CLASSIC' },
  { id: 'deep', label: 'DEEP' },
  { id: 'expedition', label: 'EXPEDITION' },
];
const TABS = tabRects(TABLES.map((t) => t.label), CONTENT_TOP - 3);
const HEAD_Y = 40;
const ROW0 = 53;
const ROW_H = 12;
const MEDALS = [YELLOW, LIGHT_GREY, ORANGE];

export class HiscoresScene extends UiScene {
  protected readonly heading = 'HIGH SCORES';
  protected override readonly track = 'hiscore' as const;
  private tab: number;
  private since = 0;
  private hotTab = -1;
  private readonly highlightTable: TableId;
  /** The new entry by its id, so it still flashes after the world tables arrive and move it. */
  private readonly highlightId: string | undefined;

  constructor(host: SceneHost, table: TableId | undefined, private readonly highlight: number | undefined) {
    super(host);
    this.tab = Math.max(0, TABLES.findIndex((t) => t.id === (table ?? 'classic')));
    this.highlightTable = table ?? 'classic';
    this.highlightId = highlight === undefined ? undefined : host.services.scores.table(this.highlightTable)[highlight]?.id;
  }

  private get table(): TableId {
    return TABLES[this.tab]?.id ?? 'classic';
  }

  override enter(): void {
    super.enter();
    this.since = 0;
    this.services.world.refresh();
  }

  protected override describe(): string {
    const rows = this.services.scores.table(this.table);
    const top = rows[0];
    return `High scores, ${TABLES[this.tab]?.label ?? ''}. ${top ? `Best: ${top.initials}, ${top.score}` : 'Empty'}`;
  }

  protected override hint(): string {
    return `◄► TABLE   ${this.words().back} BACK`;
  }

  protected override tick(dt: number): void {
    this.since += dt;
  }

  protected paint(screen: Screen): void {
    drawTabs(screen, TABLES.map((t) => t.label), this.tab, TABS, true, this.ms, this.hotTab);
    const rows = this.services.scores.table(this.table);
    screen.text('NO.', 36, HEAD_Y, FAINT, 'right');
    screen.text('NAME', 48, HEAD_Y, FAINT);
    screen.text('SCORE', 156, HEAD_Y, FAINT, 'right');
    screen.text(this.table === 'expedition' ? 'DEPTH' : 'REACHED', 176, HEAD_Y, FAINT);
    screen.text('DATE', 312, HEAD_Y, FAINT, 'right');
    screen.fill(12, HEAD_Y + 10, 296, 1, FAINT);
    if (rows.length === 0) screen.text('NO SCORES YET', 160, ROW0 + 40, MUTED, 'center');
    rows.forEach((e, i) => this.paintRow(screen, e, i));
    if (rows.some((e) => e.relaxed)) {
      screen.text('R', 12, 178, LIGHT_BLUE);
      screen.text('= PLAYED AT RELAXED SPEED', 24, 178, FAINT);
    }
  }

  private paintRow(screen: Screen, e: ScoreEntry, i: number): void {
    const y = ROW0 + i * ROW_H;
    const dx = slideOffset(i, this.since) * 4;
    const hot = this.table === this.highlightTable && (this.highlightId === undefined ? i === this.highlight : e.id === this.highlightId);
    const flash = hot ? cycle(RAINBOW, this.ms, 70) : -1;
    const colour = (normal: number): number => (hot ? flash : normal);
    screen.text(`${i + 1}.`, 36 + dx, y, colour(MEDALS[i] ?? MUTED), 'right');
    screen.text(e.initials, 48 + dx, y, colour(INK));
    screen.text(commas(e.score), 156 + dx, y, colour(INK), 'right');
    if (e.relaxed) screen.text('R', 160 + dx, y, LIGHT_BLUE);
    const reached = e.reached.replace(/^DEPTH /, '');
    screen.text(reached, 176 + dx, y, colour(ACCENT));
    screen.text(tableDate(e.date), 312 + dx, y, colour(MUTED), 'right');
    if (hot && dx === 0) drawSpinner(screen, 2, y, this.ms);
  }

  private setTab(tab: number): void {
    if (tab === this.tab || tab < 0 || tab >= TABLES.length) return this.sound('error');
    this.tab = tab;
    this.since = 0;
    this.sound('move');
    this.say(this.describe());
  }

  protected onAction(a: MenuAction): void {
    if (a === 'left') this.setTab(this.tab - 1);
    else if (a === 'right') this.setTab(this.tab + 1);
    else if (a === 'confirm') this.back();
  }

  protected override onPointer(p: PointerInput): void {
    const t = tabAt(TABS, p.x, p.y);
    if (p.kind === 'move') this.hotTab = t;
    else if (p.kind === 'down' && p.button === 0 && t >= 0 && t !== this.tab) this.setTab(t);
    else if (p.kind === 'wheel') this.setTab(this.tab + p.delta);
  }
}
